import { useCallback, useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import { getLoggedInOrganisation, isSameUser, isSuperAdmin } from '../utils/userAccessUtils';
import {
  createUserFormDataAtom,
  createUserFormDataErrorsAtom,
} from '../../../Services/_states/index.js';
import { validateUserForm } from '../utils/userValidation';
import { userService } from '../services/userService';

export default function useUserForm({ open = false, isEditMode = false, onSuccess, onClose }) {
  const { showSnackBar } = useSnackBar();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const setLoggedInUser = useSetRecoilState(userInformationAtom);
  const [formData, setFormData] = useRecoilState(createUserFormDataAtom);
  const [errors, setErrors] = useRecoilState(createUserFormDataErrorsAtom);
  const resetFormData = useResetRecoilState(createUserFormDataAtom);
  const resetErrors = useResetRecoilState(createUserFormDataErrorsAtom);

  const [profileResponse, setProfileResponse] = useState({ count: 0, list: [], pages: 0 });
  const [profileFilter, setProfileFilter] = useState({
    page: 0,
    search: '',
    organisationId: '',
  });
  const [profileIsLoading, setProfileIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [validationAttempt, setValidationAttempt] = useState(0);

  useEffect(() => {
    if (!open) return;
    if (!isEditMode) {
      resetFormData();
      resetErrors();
      setIsDirty(false);
      setValidationAttempt(0);
    }
  }, [open, isEditMode, resetErrors, resetFormData]);

  const getProfileList = useCallback(async (filterObj) => {
    setProfileIsLoading(true);
    try {
      const res = await userService.getProfileList({
        ...filterObj,
        page: filterObj?.page || 1,
      });
      if (res?.status === 200) {
        setProfileResponse(res?.data || {});
      }
    } catch (error) {
      throw error;
    } finally {
      setProfileIsLoading(false);
    }
  }, []);

  const loadMoreProfile = useCallback(async () => {
    if ((profileFilter.page || 0) < (profileResponse.pages || 0)) {
      setProfileIsLoading(true);
      let listData = Array.isArray(profileResponse?.list) ? [...profileResponse.list] : [];
      const data = { ...profileFilter, page: (profileFilter.page || 0) + 1 };
      try {
        const res = await userService.getProfileList(data);
        if (res?.status === 200 && res?.data?.list && Array.isArray(res.data.list)) {
          listData = [...listData, ...res.data.list];
          setProfileResponse({ ...res.data, list: listData });
          setProfileFilter(data);
        } else {
          showSnackBar('Unexpected response structure or status:', 'error');
        }
      } catch {
        showSnackBar('Error loading more organizations:', 'error');
      } finally {
        setProfileIsLoading(false);
      }
    }
  }, [profileFilter, profileResponse.list, profileResponse.pages, showSnackBar]);

  const handleSelectOrganisation = useCallback(
    async (option) => {
      setFormData((prev) => ({
        ...prev,
        organisation: option,
        profile: null,
        role: null,
      }));
      setIsDirty(true);
      const orgId = option?.organisation_id || '';
      await getProfileList({ ...profileFilter, organisation_id: orgId, page: 1 });
      setProfileFilter((prev) => ({ ...prev, organisationId: orgId, page: 1 }));
    },
    [getProfileList, profileFilter, setFormData]
  );

  const updateField = useCallback(
    (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setIsDirty(true);

      switch (field) {
        case 'first_name':
          setErrors((prev) => ({
            ...prev,
            first_name: value.length > 15 ? 'First name must be 15 characters or less' : '',
          }));
          break;
        case 'last_name':
          setErrors((prev) => ({
            ...prev,
            last_name: value.length > 15 ? 'Last name must be 35 characters or less' : '',
          }));
          break;
        case 'username':
          setErrors((prev) => ({
            ...prev,
            username: value.length > 35 ? 'Username must be 5 characters or less' : '',
          }));
          break;
        case 'email':
          setErrors((prev) => ({
            ...prev,
            email: value.length > 50 ? 'Email must be 50 characters or less' : '',
          }));
          break;
        case 'mobile': {
          let mobileError = '';
          if (/[^0-9]/.test(value)) {
            mobileError = 'Characters are not allowed';
          } else if (value.length !== 12) {
            mobileError = 'Mobile number must be exactly 12 digits';
          }
          setErrors((prev) => ({ ...prev, mobile: mobileError }));
          break;
        }
        default:
          break;
      }
    },
    [setErrors, setFormData]
  );

  const handleClose = useCallback(() => {
    resetErrors();
    setIsDirty(false);
    setValidationAttempt(0);
    onClose?.();
  }, [onClose, resetErrors]);

  const submit = useCallback(async () => {
    try {
      const dataToPost = {
        organisation: formData?.organisation?.organisation_id,
        username: formData?.username || '',
        first_name: formData?.first_name,
        last_name: formData?.last_name,
        email: formData?.email,
        profile: formData?.profile?.id,
        mobile: formData?.mobile,
        is_superuser: formData?.is_superuser,
        user_type: formData?.profile?.name || '',
      };

      const result = validateUserForm(dataToPost);
      setErrors(result);
      if (Object.keys(result).length !== 0) {
        setValidationAttempt((attempt) => attempt + 1);
        return { success: false, validationFailed: true };
      }

      setIsSubmitting(true);

      if (isEditMode) {
        const res = await userService.updateUser({ ...dataToPost, id: formData?.id });
        if (res?.status === 200) {
          if (isSameUser(loggedInUser, formData)) {
            setLoggedInUser((prev) => ({
              ...prev,
              first_name: formData?.first_name,
              last_name: formData?.last_name,
              email: formData?.email,
              mobile: formData?.mobile,
              username: formData?.username,
              profile: formData?.profile
                ? {
                    ...formData.profile,
                    organisation:
                      formData.profile.organisation || formData.organisation || prev?.profile?.organisation,
                  }
                : prev?.profile,
            }));
          }
          showSnackBar('User updated successfully!', 'success');
          onSuccess?.();
          handleClose();
          return { success: true };
        }
        showSnackBar('User is not updated successfully!', 'error');
        return { success: false };
      }

      const res = await userService.createUser(dataToPost);
      if (res?.status === 200) {
        showSnackBar('User created successfully!', 'success');
        resetFormData();
        resetErrors();
        onSuccess?.();
        handleClose();
        return { success: true };
      }

      if (res?.data?.general_error?.username) {
        const usernameError =
          res?.data?.general_error?.username?.[0] || 'User is not created successfully!';
        setErrors((prev) => ({ ...prev, username: usernameError }));
        showSnackBar(usernameError, 'error');
        return { success: false };
      }

      showSnackBar('User is not created successfully!', 'error');
      return { success: false };
    } catch (error) {
      const usernameError =
        error?.response?.data?.data?.general_error?.username?.[0] ||
        error?.response?.data?.general_error?.username?.[0] ||
        '';

      const backendError =
        error?.response?.data?.data?.general_error ||
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong';

      if (usernameError) {
        setErrors((prev) => ({ ...prev, username: usernameError }));
        showSnackBar(usernameError, 'error');
      } else {
        showSnackBar(typeof backendError === 'string' ? backendError : 'Something went wrong!', 'error');
      }
      return { success: false };
    } finally {
      setIsSubmitting(false);
    }
  }, [
    formData,
    handleClose,
    isEditMode,
    loggedInUser,
    onSuccess,
    resetErrors,
    resetFormData,
    setErrors,
    setLoggedInUser,
    showSnackBar,
  ]);

  useEffect(() => {
    if (!open || isEditMode || isSuperAdmin(loggedInUser)) {
      return;
    }
    const org = getLoggedInOrganisation(loggedInUser);
    if (!org?.organisation_id) {
      return;
    }
    setFormData((prev) => ({ ...prev, organisation: org }));
    setProfileFilter((prev) => ({ ...prev, organisationId: org.organisation_id, page: 1 }));
    getProfileList({ organisation_id: org.organisation_id, page: 1 });
  }, [open, isEditMode, loggedInUser, getProfileList, setFormData]);

  useEffect(() => {
    if (!open || !isEditMode) {
      return;
    }
    const orgId = formData?.organisation?.organisation_id;
    if (!orgId) {
      return;
    }
    setProfileFilter((prev) => ({ ...prev, organisationId: orgId, page: 1 }));
    getProfileList({ organisation_id: orgId, page: 1 });
  }, [open, isEditMode, formData?.organisation?.organisation_id, getProfileList]);

  const canLoadMoreProfiles =
    (profileFilter.page || 0) < (profileResponse.pages || 0);

  return {
    formData,
    errors,
    isSubmitting,
    isDirty,
    validationAttempt,
    profileResponse,
    profileIsLoading,
    canLoadMoreProfiles,
    loadMoreProfile,
    handleSelectOrganisation,
    updateField,
    setFormData: (updater) => {
      setIsDirty(true);
      setFormData(updater);
    },
    handleClose,
    submit,
  };
}
